import { useState, memo, useEffect } from 'react'
import { Template } from './Template'
import type { KcProps } from 'keycloakify'
import type { KcContext } from '../kcContext'
import { useKcMessage } from 'keycloakify/lib/i18n/useKcMessage'
import { useCssAndCx } from 'tss-react'
import { useConstCallback } from 'powerhooks/useConstCallback'
import { Button, Input, Form, Icon } from '@xanadu_ai/xanadu-component-library'

import googleColorSvg from '../assets/google.svg'
import './Login.css'

type KcContext_Login = Extract<KcContext, { pageId: 'login.ftl' }>

export const Login = memo(
  ({ kcContext, ...props }: { kcContext: KcContext_Login } & KcProps) => {
    const {
      social,
      realm,
      url,
      usernameEditDisabled,
      login,
      auth,
      registrationDisabled,
    } = kcContext

    const { msg, msgStr } = useKcMessage()

    const { cx } = useCssAndCx()

    const [isLoginButtonDisabled, setIsLoginButtonDisabled] = useState(false)
    const [userEmail, updateUserEmail] = useState('')
    const [userPassword, updateUserPassword] = useState('')

    // eslint-disable-next-line
    const onSubmit = useConstCallback(
      // eslint-disable-next-line no-sequences
      () => (setIsLoginButtonDisabled(true), true)
    )

    useEffect(() => {
      setIsLoginButtonDisabled(!userEmail || !userPassword)
    }, [userEmail, userPassword])

    return (
      <Template
        {...{ kcContext, ...props }}
        doFetchDefaultThemeResources={false}
        displayInfo={social.displayInfo}
        displayWide={realm.password && social.providers !== undefined}
        headerNode="Welcome to Xanadu Cloud"
        headerDescription="Join a community of learners, researchers, and developers to build the next generation of quantum technology."
        formNode={
          <div
            id="kc-form"
            className={cx(
              realm.password &&
                social.providers !== undefined &&
                props.kcContentWrapperClass
            )}
          >
            {realm.password && social.providers !== undefined && (
              <div
                id="kc-social-providers"
                className={cx(
                  props.kcFormSocialAccountContentClass,
                  props.kcFormSocialAccountClass
                )}
              >
                {social.providers.map((p) => (
                  <a
                    key={p.providerId}
                    href={p.loginUrl}
                    id={`zocial-${p.alias}`}
                    className={cx('SSO-Button', p.providerId)}
                  >
                    {p.providerId === 'google' ? <img src={googleColorSvg} className="mr-4" alt="Google Logo" /> : <Icon icon={['fa-brands', `fa-${p.providerId}`]} size="lg" className="mr-4 my-auto text-gray-1" />}
                    <span className="text-gray-2 my-auto font-medium">Sign in with {p.displayName}</span>
                  </a>
                ))}
                <p className="mb-4">Or, sign in with your email address:</p>
              </div>
            )}
            <div
              id="kc-form-wrapper"
              className={cx(
                realm.password &&
                  social.providers && [
                    props.kcFormSocialAccountContentClass,
                    props.kcFormSocialAccountClass,
                  ]
              )}
            >
              {realm.password && (
                <Form
                  formName="kc-form-login"
                  onSubmit={onSubmit}
                  action={url.loginAction}
                  method="post"
                >
                  <>
                    <div className={cx(props.kcFormGroupClass)}>
                      <Input
                        testId="username"
                        className={cx(props.kcInputClass)}
                        formName="kc-form-login"
                        fieldName="username"
                        fieldLabel={
                          !realm.loginWithEmailAllowed ? 'Username' : 'Email'
                        }
                        type="text"
                        value={userEmail}
                        onChange={(e) => updateUserEmail(e)}
                        {...(usernameEditDisabled
                          ? { disabled: true }
                          : {
                              autoFocus: true,
                              autoComplete: 'off',
                            })}
                      />
                    </div>
                    <div className={cx(props.kcFormGroupClass)}>
                      <Input
                        formName="kc-form-login"
                        fieldName="password"
                        fieldLabel="Password"
                        testId="password"
                        className={cx(props.kcInputClass)}
                        type="password"
                        value={userPassword}
                        onChange={(e) => updateUserPassword(e)}
                      />
                    </div>
                    <div
                      className={cx(
                        props.kcFormGroupClass,
                        props.kcFormSettingClass
                      )}
                    >
                      <div id="kc-form-options">
                        {realm.rememberMe && !usernameEditDisabled && (
                          <div className="checkbox">
                            <label>
                              <input
                                tabIndex={3}
                                id="rememberMe"
                                name="rememberMe"
                                type="checkbox"
                                {...(login.rememberMe
                                  ? {
                                      checked: true,
                                    }
                                  : {})}
                              />
                              {msg('rememberMe')}
                            </label>
                          </div>
                        )}
                      </div>
                      <div
                        className={`${cx(
                          props.kcFormOptionsWrapperClass
                        )} forgot-password`}
                      >
                        {realm.resetPasswordAllowed && (
                          <a
                            data-testid="forgot-password"
                            id="forgot-password"
                            tabIndex={5}
                            href={url.loginResetCredentialsUrl}
                          >
                            {msg('doForgotPassword')}
                          </a>
                        )}
                      </div>
                    </div>
                    <div
                      id="kc-form-buttons"
                      className={cx(props.kcFormGroupClass)}
                    >
                      <input
                        type="hidden"
                        id="id-hidden-input"
                        name="credentialId"
                        {...(auth?.selectedCredential !== undefined
                          ? {
                              value: auth.selectedCredential,
                            }
                          : {})}
                      />
                      <Button
                        testId="login-button"
                        className="justify-center"
                        buttonType="submit"
                        label={msgStr('doLogIn')}
                        disabled={isLoginButtonDisabled}
                      />
                    </div>
                  </>
                </Form>
              )}
            </div>
          </div>
        }
        infoNode={
          realm.password &&
          realm.registrationAllowed &&
          !registrationDisabled && (
            <div id="kc-registration">
              <span>{msg('noAccount')}</span>
              <a
                id="kc-signup"
                data-testid="signup-button"
                tabIndex={6}
                href={url.registrationUrl}
                className="ml-1"
              >
                {msg('doRegister')}
              </a>
            </div>
          )
        }
      />
    )
  }
)
